/*
 * Pixi SIN `eval`. Tiene que ir antes que nada de Pixi.
 *
 * Por defecto Pixi arma parte de su codigo de render en caliente con
 * `new Function`, y eso obliga a poner `'unsafe-eval'` en la CSP — que es
 * justamente la directiva que se quiere evitar. Este modulo cambia esas rutas
 * por versiones interpretadas.
 *
 * El nombre engaña: `pixi.js/unsafe-eval` es lo que hace que Pixi funcione
 * DONDE NO se permite unsafe-eval, no lo que lo activa. Sin esta linea, con la
 * CSP puesta el juego no arranca: "Current environment does not allow
 * unsafe-eval".
 */
import { primaryKey } from "./input/bindings.js";
import "pixi.js/unsafe-eval";
import { Application } from "pixi.js";
import { revealApp, syncDocumentLang, t } from "./i18n/index.js";
import {
  AI,
  AIM,
  ACTIVE_MATCH_MODE,
  BALL,
  COLORS,
  GK,
  KICK,
  MATCH,
  MATCH_MODES,
  PLAYER,
  POSSESSION,
  TEAM_INSTRUCTIONS,
  resetMatchFormat,
  setMatchFormat,
  resetTeamInstructions,
  TACTICS,
  TEAM_IDENTITY,
  setMatchMode,
  DEFAULT_PITCH_SKIN,
  loadStoredPitchSkin,
  applyTeamSetup,
  loadStoredTeamSetup,
  applyWeeklyRule,
  clearWeeklyRule,
  ruleById,
  type AssistLevel,
  type MatchModeId,
  type TeamSide,
  type WireInputFrame,
  type OnlineRoom,
  type RoomMoveTarget,
  type FriendEntry,
} from "@golazo/shared";
import { createAiController } from "./ai/brain.js";
import { createProfileBook, summariseMatch } from "./game/profile.js";
import { createProfileSync, parseProfile } from "./game/profileSync.js";
import { createFriendCard } from "./ui/friendCard.js";
import { createSocialLayer, type SocialLayer } from "./ui/socialLayer.js";
import { mountMatesStrip, type Mate } from "./ui/matesStrip.js";
import { careerRewardHtml, createCareerScreen } from "./ui/careerScreen.js";
import { createClipPlayer, type ClipPlayerHandle } from "./ui/clipPlayer.js";
import { mountResultCard, type ResultCardHandle } from "./ui/resultCard.js";
import { createLoop, type Loop } from "./game/loop.js";
import { finalStatsHtml, mvpBlockHtml } from "./ui/scoreboard.js";
import { summaryLayoutHtml, yourMatchHtml } from "./ui/resultSummary.js";
import { createMatch, type Match, type MatchOptions } from "./game/match.js";
import { createMenuMusic } from "./juice/menuMusic.js";
import { createOnlineClient } from "./online/client.js";
import { firstSeat, secondSeat } from "./game/localPlayers.js";
import { localPairAvailable } from "./mobile/platform.js";
import { createDirectMessages } from "./online/directMessages.js";
import { claimCareerUnlocks, enforceUnlocks, unlockStore } from "./career/unlocks.js";
import { beginMatch, recordMatch, resolvePending } from "./career/career.js";
import { careerMatchSetup, performanceOf, type CareerMatchSetup } from "./career/matchSetup.js";
import { loadCareer, saveCareer } from "./career/store.js";
import type { Objective } from "./career/model.js";
import { objectiveKey, objectiveProgress } from "./career/phoneTalk.js";
import { escapeHtml } from "./ui/careerText.js";
import { applyCareerMatch, careerMatchOptions } from "./game/careerMatch.js";
import { createCareerCloud } from "./game/careerCloud.js";
import { careerResultHtml } from "./ui/careerMatchResult.js";
import { confirmDialog } from "./ui/confirmDialog.js";
import { createCareerHub, seasonTeaser } from "./ui/careerHub.js";
import { WORLD_TEASER_KEY, createWorldScreen } from "./ui/worldScreen.js";
import { createWorldService } from "./online/world.js";
import { difficultySkill, type Difficulty } from "./game/difficulty.js";
import { rememberPlayers } from "./online/recentPlayers.js";
import {
  createNicknameService,
  loadNickname,
  registerNicknameEditor,
} from "./online/nickname.js";
import { createNicknameDialog } from "./ui/nicknameDialog.js";
import { createRoomChat } from "./online/roomChat.js";
import { createFriendsStore } from "./online/friends.js";
import { createPartyStore } from "./online/party.js";
import { createLobbyChat } from "./online/lobbyChat.js";
import { createMatchChat, type MatchChatHandle } from "./ui/matchChat.js";
import { isTypingTarget } from "./input/keyboard.js";
import { createLockstep } from "./online/lockstep.js";
import { createEntryGate } from "./ui/entryGate.js";
import { createMainMenu } from "./ui/mainMenu.js";
import { createMatchChrome } from "./ui/matchChrome.js";
import { createMenu } from "./ui/menu.js";
import { preloadTextures } from "./render/textures.js";
import { createAdaptiveQuality } from "./render/adaptiveQuality.js";
import { onCameraModeChange } from "./render/cameraModes.js";
import { onFullscreenChange } from "./mobile/fullscreen.js";
import { createNetStatus } from "./ui/netStatus.js";
import { replayProgress } from "./game/replay.js";
import { createOnlineLobby } from "./ui/onlineLobby.js";
import { initMobileShell } from "./mobile/shell.js";
import { installTouchErgonomics } from "./mobile/touchErgonomics.js";
import { isMobileHandheld, wantsTouchControls } from "./mobile/detect.js";
import { createCommentary, type CaptionEvent } from "./game/commentary.js";
import { createMatchCaption, type CaptionContent } from "./ui/matchCaption.js";
import { createControlsStrip, wantsControlsStrip, type ControlsStrip } from "./ui/controlsStrip.js";
import { scoreBugClearance } from "./ui/hud.js";
import { isCompact } from "./mobile/viewport.js";
import { STYLE_BLURB, STYLE_LABEL } from "./career/phone.js";
import { enterFullscreen, prefersFullscreen, supportsFullscreen } from "./mobile/fullscreen.js";

function wsUrl(): string {
  const env = import.meta.env.VITE_WS_URL as string | undefined;
  if (env) return env;
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.hostname}:8787`;
}

/** ?sala=<id> — se ha llegado por un enlace de invitacion. */
function parseInviteRoom(): string | null {
  const raw = new URLSearchParams(location.search).get("sala");
  return raw?.trim() ? raw.trim() : null;
}

/**
 * ?de=<codigo> — el codigo de amigo de quien paso el enlace. Se consume: si
 * se quedara en la barra y alguien copiara la direccion para pasarla a otro,
 * le haria amigo del primero.
 */
function parseInviteFrom(): string | null {
  const url = new URL(location.href);
  const raw = url.searchParams.get("de");
  if (raw === null) return null;
  url.searchParams.delete("de");
  try {
    history.replaceState(history.state, "", url);
  } catch {
    // Se queda en la barra; como mucho, una solicitud de mas.
  }
  return raw.trim() || null;
}

/**
 * ?carrera — abre la pantalla de la carrera. ?carrera=nueva abre la de crear
 * jugador. Un enlace directo, como el de las salas.
 */
function parseCareerParam(): "continue" | "new" | null {
  const url = new URL(location.href);
  const raw = url.searchParams.get("carrera");
  if (raw === null) return null;
  /*
   * Se consume: si se quedara en la barra, cada recarga (y cada recarga en
   * caliente de Vite) volveria a empezar una carrera NUEVA, y el partido a
   * medias de la anterior se daria por jugado sin ti.
   */
  url.searchParams.delete("carrera");
  try {
    history.replaceState(history.state, "", url);
  } catch {
    // Sin historial se queda en la barra; como mucho, repite.
  }
  return raw === "nueva" ? "new" : "continue";
}

/** ?train=7v7 o ?mode=7v7 — salta menus y abre partido local vs IA. */
function parseTrainMode(): MatchModeId | null {
  const params = new URLSearchParams(location.search);
  const raw = params.get("train") ?? params.get("mode");
  if (!raw) return null;
  return raw in MATCH_MODES ? (raw as MatchModeId) : null;
}

async function boot(): Promise<void> {
  // El <html lang> lo leen lectores de pantalla y buscadores.
  syncDocumentLang();
  // Gestos del navegador que estorban en tactil (iOS ignora user-scalable=no).
  installTouchErgonomics();
  /*
   * Equipaciones y nombres, ANTES de que exista ningun partido.
   *
   * El modal de personalizar tambien los aplica, pero no basta: se puede entrar
   * directo a un partido por `?mode=`, por un enlace de sala o desde la
   * carrera, y por esos caminos el modal no se abre. Salir a jugar con la
   * camiseta que elegiste no puede depender de por donde has entrado.
   */
  // Solo lo que tienes: las equipaciones se ganan en la carrera.
  applyTeamSetup(enforceUnlocks(loadStoredTeamSetup(), unlockStore().get()));
  const mount = document.getElementById("stage");
  if (!mount) throw new Error("Falta el contenedor #stage en index.html");

  const mobileShell = initMobileShell();

  const wsServerUrl = wsUrl();
  // El apodo se PIDE en cada `hello`: el servidor lo reserva si esta libre.
  const onlineClient = createOnlineClient({ url: wsServerUrl, reconnect: true, nickname: loadNickname });
  /*
   * Apodos unicos: el servicio que los pide y confirma, y el dialogo para
   * cambiarlo desde cualquier pantalla (lobby, menu, personalizar).
   */
  const nicknames = createNicknameService(onlineClient);
  let nicknameDialog = createNicknameDialog(nicknames);
  registerNicknameEditor((notice) => nicknameDialog.open(notice));
  /** Aviso de "tu nombre es de otro" que llego jugando: se enseña al volver. */
  let pendingNicknameNotice: string | null = null;
  // La conversacion de la sala: la misma en la pantalla de la sala y dentro
  // del partido. Ver `online/roomChat.ts`.
  const roomChat = createRoomChat(onlineClient);
  // Amigos y chat del lobby: fuera del lobby porque tambien avisan en partido.
  const friendsStore = createFriendsStore(onlineClient);
  const lobbyChat = createLobbyChat(onlineClient);
  // Privados con los amigos: tambien avisan dentro del partido.
  const directMessages = createDirectMessages(onlineClient);
  /** Tu grupo: amigos que van juntos, como en Fortnite (ver `online/party.ts`). */
  const partyStore = createPartyStore(onlineClient);
  // Estadisticas mundiales: clasificaciones, fichas y records del servidor.
  const worldService = createWorldService(onlineClient);
  // Las equipaciones de competicion las da el servidor (temporadas cerradas):
  // se suman a las tuyas, nunca quitan.
  onlineClient.onMessage((msg) => {
    if (msg.type === "welcome" && msg.worldKits?.length) unlockStore().merge({ kits: msg.worldKits, custom: false });
  });
  /*
   * La sala del partido en curso, tal y como la manda el servidor. La usa el
   * panel del anfitrion dentro del partido (banquillo, meter, parar).
   */
  let liveRoom: OnlineRoom | null = null;
  const liveRoomListeners = new Set<() => void>();
  onlineClient.onMessage((msg) => {
    const id = onlineClient.activeRoomId;
    if (!id) return;
    const next =
      msg.type === "room:update" && msg.room.id === id
        ? msg.room
        : msg.type === "rooms:sync"
          ? msg.rooms.find((r) => r.id === id) ?? null
          : null;
    if (!next) return;
    liveRoom = next;
    for (const fn of liveRoomListeners) fn();
  });
  const matchRoomControls = {
    room: () => liveRoom,
    move: (clientId: string, to: RoomMoveTarget) => {
      const id = onlineClient.activeRoomId;
      if (id) onlineClient.send({ type: "room:move", roomId: id, clientId, to });
    },
    stop: () => {
      const id = onlineClient.activeRoomId;
      if (id) onlineClient.send({ type: "room:stop", roomId: id });
    },
    onChange: (fn: () => void) => {
      liveRoomListeners.add(fn);
      return () => liveRoomListeners.delete(fn);
    },
  };

  /*
   * Musica SOLO en los menus (lo pidio el usuario el 2026-09-27). En el campo
   * no suena nada: se oyen el balon, los golpes y la grada (ver `juice/`).
   */
  const menuMusic = createMenuMusic();

  // Dos perfiles: contra personas (online) y contra la IA (offline). Ver profile.ts.
  const profileBook = createProfileBook();

  /*
   * Sincronizacion de la carrera con la cuenta del server.
   *
   * Se engancha a TODOS los mensajes, no solo a `welcome`, porque tambien tiene
   * que reaccionar al canje de un codigo de recuperacion. Y sube la carrera al
   * terminar cada partido: si solo se subiera al conectar, cerrar el navegador
   * despues de jugar perderia la sesion entera.
   */
  const profileSync = createProfileSync(profileBook, (msg) => onlineClient.send(msg));
  onlineClient.onMessage((msg) => {
    profileSync.handle(msg);
    if (msg.type === "welcome") {
      careerScreen.setGoogle(msg.googleClientId, msg.googleEmail);
      entryGate.setGoogle(msg.googleClientId);
    }
    if (msg.type === "account:recovery") careerScreen.showCode(msg.code);
    if (msg.type === "account:redeemed") {
      entryGate.googleAccepted(msg.nickname);
      careerScreen.setGoogle(null, msg.googleEmail);
      careerScreen.showRedeemResult(
        true,
        msg.googleEmail
          ? t("Cuenta conectada. Ya no la pierdes.")
          : t("Listo. Ahora juegas como {nick}.", { nick: msg.nickname }),
      );
    }
    if (
      msg.type === "error" &&
      (msg.code === "bad_recovery" || msg.code === "same_account" || msg.code === "google_bad")
    ) {
      careerScreen.showRedeemResult(false, msg.message);
      entryGate.showError(msg.message);
    }
  });
  /**
   * Puerta de entrada. Solo se enseña si el jugador NO ha entrado nunca: a
   * quien ya eligio se le manda derecho al menu, porque enseñarla en cada
   * arranque seria devolver por la puerta de atras la friccion que se queria
   * quitar.
   *
   * La marca es propia y NO el token: conectarse al server crea token solito,
   * asi que usarlo como señal hacia que la puerta no saliera nunca — el
   * `connect()` corre antes. Este flag solo lo pone la puerta al cerrarse.
   */
  const ENTERED_KEY = "golazo.entered";

  const hasEntered = (): boolean => {
    try {
      return localStorage.getItem(ENTERED_KEY) === "1";
    } catch {
      // Sin almacenamiento se enseña cada vez; es lo unico honesto que se puede
      // hacer si no se puede recordar nada.
      return false;
    }
  };

  const markEntered = (): void => {
    try {
      localStorage.setItem(ENTERED_KEY, "1");
    } catch {
      // Da igual: como mucho vuelve a preguntar.
    }
  };

  const entryGate = createEntryGate(document.body, {
    onGoogle: (credential) => onlineClient.send({ type: "account:google", credential }),
    onCode: (code) => onlineClient.send({ type: "account:redeem", code }),
    nickname: nicknames,
  });

  /*
   * El servidor no pudo darte el nombre que tenias guardado: otra cuenta lo
   * reservo antes (los apodos son unicos desde el 2026-09-26). Se pide otro
   * en el acto, salvo a mitad de partido: entonces, al volver al menu.
   */
  nicknames.onTaken((requested) => {
    const notice = t("«{nick}» ya es de otro jugador. Elige otro: por ahora juegas como {now}.", {
      nick: requested,
      now: loadNickname(),
    });
    if (match && !ending) pendingNicknameNotice = notice;
    else void nicknameDialog.open(notice);
  });

  /*
   * La ficha de un amigo comparada con la tuya. Una sola, para "Mis
   * estadisticas" y para el lobby: se pide su perfil ONLINE al servidor (solo
   * se da a amigos) y se compara con el tuyo online.
   */
  /**
   * Ir a hacer algo con un amigo desde CUALQUIER pantalla: escribirle, entrar
   * en su sala, montar una sala con el, pasar el enlace a alguien nuevo.
   *
   * Con el lobby abierto se hace alli mismo. Si no, se deja el destino puesto,
   * se cierra la pantalla que haya (menu, estadisticas, mundial, carrera,
   * resultados) y el bucle del menu abre el lobby con el. Antes esto solo
   * sabia salir de "Mis estadisticas": un "Unirse" desde el menu no iba a
   * ninguna parte. Jugando, nada: un aviso no te saca de un partido.
   */
  interface FriendTarget {
    dm?: string;
    joinRoomId?: string;
    /** Con pase (invitacion o grupo): se entra sin que pida contraseña. */
    pass?: boolean;
    playWith?: string;
    inviteNew?: boolean;
  }
  const goToFriend = (target: FriendTarget): void => {
    friendCard.close();
    social.closePanel();
    if (target.joinRoomId) friendsStore.dropInvite(target.joinRoomId);
    if (onlineLobby.isVisible()) {
      if (target.dm) onlineLobby.openChat(target.dm);
      if (target.joinRoomId) onlineLobby.joinRoom(target.joinRoomId, target.pass);
      if (target.playWith) onlineLobby.playWith(target.playWith);
      if (target.inviteNew) onlineLobby.inviteNew();
      return;
    }
    if (match && !ending) return;
    queuedHome = { lobby: true, ...target };
    // En los resultados: como pulsar "Salir" (en online, deja la sala).
    if (chrome.resultOpen()) {
      chrome.dismissResult("menu");
      return;
    }
    careerScreen.close();
    worldScreen.leave();
    careerHub.leave();
    privateMenu.leave();
    mainMenu.leave();
  };
  const friendCardActions = {
    message: (clientId: string) => goToFriend({ dm: clientId }),
    join: (friend: FriendEntry) => {
      if (friend.roomId) goToFriend({ joinRoomId: friend.roomId });
    },
    // Con sala abierta, a ella; sin sala, se monta una para los dos.
    invite: (clientId: string) =>
      onlineLobby.isVisible() && onlineLobby.canInvite()
        ? onlineLobby.inviteFriend(clientId)
        : goToFriend({ playWith: clientId }),
    canInvite: () => onlineLobby.isVisible() && onlineLobby.canInvite(),
    remove: (clientId: string) => friendsStore.remove(clientId),
    block: (clientId: string) => friendsStore.block(clientId),
  };
  let friendCard = createFriendCard(document.body, friendCardActions);
  const friendEntry = (clientId: string): FriendEntry | null =>
    friendsStore.state()?.friends.find((f) => f.clientId === clientId) ?? null;
  const openFriend = (clientId: string, nickname: string): void => {
    const friend = friendEntry(clientId);
    friendCard.loading(clientId, nickname, friend);
    void friendsStore.profileOf(clientId).then((got) => {
      // Cerrada mientras cargaba, o abierta ya para otro: no se pisa.
      if (friendCard.openFor() !== clientId) return;
      friendCard.open({
        clientId,
        nickname: got?.nickname ?? nickname,
        profile: got ? parseProfile(got.profile) : null,
        mine: profileBook.online.get(),
        friend: friendEntry(clientId),
        self: clientId === onlineClient.clientId,
      });
    });
  };
  // Su estado en vivo mientras su ficha esta abierta.
  friendsStore.subscribe(() => {
    const open = friendCard.openFor();
    if (open) friendCard.updateFriend(friendEntry(open));
  });

  let careerScreen = createCareerScreen(document.body);
  /*
   * Lo que la pantalla de estadisticas necesita de fuera. En una funcion porque
   * se rehace al cambiar de idioma, y antes solo se enganchaba la primera vez:
   * tras cambiar de idioma la tarjeta de cuenta desaparecia.
   */
  const wireCareerScreen = (): void => {
    // La tarjeta de cuenta solo aparece cuando hay con quien hablar: sin server
    // no hay codigo que sacar, y enseñar un boton muerto es peor que no tenerlo.
    careerScreen.bindAccount({
      requestCode: () => onlineClient.send({ type: "account:recovery" }),
      redeem: (code) => onlineClient.send({ type: "account:redeem", code }),
      google: (credential) => onlineClient.send({ type: "account:google", credential }),
    });
    careerScreen.setSocial({ board: () => friendsStore.board(), open: openFriend });
  };
  wireCareerScreen();

  /** Lo que se ensena en la pastilla del menu: lo mas vivo que tenga la carrera. */
  const careerTeaser = (): string => {
    const p = profileBook.main();
    if (p.dayStreak >= 2) return `🔥 ${t("{n} días seguidos", { n: p.dayStreak })}`;
    if (p.winStreak >= 2) return t("{n} victorias seguidas", { n: p.winStreak });
    if (p.weekly.points > 0) return t("{n} pts esta semana", { n: p.weekly.points });
    if (p.totals.matches > 0) return t("{n} partidos", { n: p.totals.matches });
    return "";
  };

  /** Tu sitio en el mundo, tal y como lo dejo la ultima visita a la pantalla. */
  const worldTeaser = (): string => {
    try {
      return localStorage.getItem(WORLD_TEASER_KEY) ?? "";
    } catch {
      return "";
    }
  };
  const worldScreenOptions = {
    service: worldService,
    isFriend: (id: string) => friendsStore.isFriend(id),
    addFriend: (id: string) => friendsStore.addById(id),
  };
  let worldScreen = createWorldScreen(document.body, worldScreenOptions);
  /*
   * Los amigos fuera del lobby: avisos con boton en cualquier pantalla y el
   * panel del menu. Antes que el menu, que al montarse ya pregunta cuantos
   * hay conectados.
   */
  const makeSocial = (): SocialLayer =>
    createSocialLayer({
      friends: friendsStore,
      dms: directMessages,
      party: partyStore,
      online: () => onlineClient.state === "online",
      where: () => (match && !ending ? "match" : match ? "results" : onlineLobby.isVisible() ? "lobby" : "menu"),
      actions: {
        join: (roomId) => goToFriend({ joinRoomId: roomId, pass: friendsStore.invites().some((i) => i.roomId === roomId) }),
        playWith: (clientId) => goToFriend({ playWith: clientId }),
        openDm: (clientId) => goToFriend({ dm: clientId }),
        openCard: (clientId, nickname) => openFriend(clientId, nickname),
        inviteNew: () => goToFriend({ inviteNew: true }),
        // El lider entro en una sala: ahi vas, desde donde estes.
        follow: (roomId) => goToFriend({ joinRoomId: roomId, pass: true }),
      },
    });
  let social = makeSocial();
  const menuSocial = {
    summary: () => social.summary(),
    subscribe: (fn: () => void) => social.subscribe(fn),
    open: () => social.openPanel(),
    panelOpen: () => social.panelOpen(),
  };
  let mainMenu = createMainMenu(document.body, { music: menuMusic, careerTeaser, worldTeaser, social: menuSocial });
  /** A quien no ha jugado nunca contra la IA, el Barrio; a quien ya jugaba, la de siempre. */
  const defaultDifficulty = (): Difficulty =>
    profileBook.offline.get().totals.matches > 0 ? "primera" : "barrio";
  let privateMenu = createMenu(document.body, { careerTeaser: seasonTeaser, defaultDifficulty });
  /** La carrera: crear jugador, portada, pestañas, ofertas, salon de la fama. */
  let careerHub = createCareerHub(document.body);
  /*
   * La carrera offline en la cuenta, en su propio hueco (aparte de las
   * estadisticas): se junta al conectar y se sube despues de cada paso.
   */
  const careerCloud = createCareerCloud({
    send: (msg) => onlineClient.send(msg),
    onState: (fn) => onlineClient.onState(fn),
    onAdopted: () => careerHub.refresh(),
  });
  onlineClient.onMessage((msg) => careerCloud.handle(msg));
  let onlineLobby = createOnlineLobby(document.body, {
    client: onlineClient,
    chat: roomChat,
    friends: friendsStore,
    lobbyChat,
    openFriend: (id: string, nick: string) => openFriend(id, nick),
    directMessages,
    party: partyStore,
    openSocial: () => social.openPanel(),
    serverUrl: import.meta.env.DEV ? wsServerUrl : undefined,
  });
  let chrome = createMatchChrome(document.body);

  /**
   * Rehace la interfaz de menus con el idioma actual.
   *
   * Es lo que antes hacia `location.reload()`, pero sin volver a montar el
   * juego: cada pantalla cuece su texto al construirse (`t()` es sincrona), asi
   * que la unica forma de re-traducirlas es destruirlas y crearlas de nuevo.
   * Cuesta milisegundos; recargar costaba el arranque entero —Pixi, texturas,
   * audio— y se veia como una pantalla de carga en mitad de un menu.
   *
   * Se rehacen TODAS las pantallas de menu, no solo la que estas mirando: si
   * solo se rehiciera el menu principal, entrarias al lobby y seguiria en el
   * idioma viejo.
   */
  const rebuildMenus = (): void => {
    careerScreen.dispose();
    mainMenu.dispose();
    privateMenu.dispose();
    onlineLobby.dispose();
    chrome.dispose();

    careerScreen = createCareerScreen(document.body);
    wireCareerScreen();
    friendCard.dispose();
    friendCard = createFriendCard(document.body, friendCardActions);
    social.dispose();
    social = makeSocial();
    mainMenu = createMainMenu(document.body, { music: menuMusic, careerTeaser, worldTeaser, social: menuSocial });
    worldScreen.dispose();
    worldScreen = createWorldScreen(document.body, worldScreenOptions);
    privateMenu = createMenu(document.body, { careerTeaser: seasonTeaser, defaultDifficulty });
    careerHub.dispose();
    careerHub = createCareerHub(document.body);
    onlineLobby = createOnlineLobby(document.body, {
      client: onlineClient,
      chat: roomChat,
      friends: friendsStore,
      lobbyChat,
      openFriend,
      directMessages,
      party: partyStore,
      openSocial: () => social.openPanel(),
      serverUrl: import.meta.env.DEV ? wsServerUrl : undefined,
    });
    chrome = createMatchChrome(document.body);
    nicknameDialog.dispose();
    nicknameDialog = createNicknameDialog(nicknames);
    syncDocumentLang();
  };

  const disconnectOverlay = document.createElement("div");
  disconnectOverlay.className = "net-disconnect-overlay is-hidden";
  disconnectOverlay.innerHTML = `
    <div class="net-disconnect-card">
      <h2 data-net-title>${t("Conexión perdida")}</h2>
      <p data-net-body>${t("Esperando reconexión con el servidor…")}</p>
      <div class="net-disconnect-actions">
        <button type="button" class="menu-play" data-net-here hidden>${t("Jugar aquí")}</button>
        <button type="button" class="menu-play" data-net-reload hidden>${t("Recargar")}</button>
        <button type="button" class="menu-play" data-net-exit>${t("Salir al menú")}</button>
      </div>
    </div>
  `;
  document.body.appendChild(disconnectOverlay);
  const netHere = disconnectOverlay.querySelector<HTMLButtonElement>("[data-net-here]")!;
  const netExit = disconnectOverlay.querySelector<HTMLButtonElement>("[data-net-exit]")!;
  const netReload = disconnectOverlay.querySelector<HTMLButtonElement>("[data-net-reload]")!;
  // Version nueva: recargar es lo unico que arregla un cliente viejo.
  netReload.addEventListener("click", () => window.location.reload());
  /*
   * La tarjeta de "abierto en otra pestaña" fuera de un partido: la misma
   * tarjeta, pero sin partido que parar, y su segundo boton solo la cierra
   * (Local / IA y la carrera no necesitan conexion).
   */
  let elsewhereCard = false;
  /** "Seguir sin conexión": no se vuelve a sacar al pasar por el menu. */
  let elsewhereDismissed = false;
  /** Lo mismo para el aviso de version nueva. */
  let outdatedDismissed = false;
  const hideElsewhereCard = (): void => {
    if (!elsewhereCard) return;
    elsewhereCard = false;
    netHere.hidden = true;
    netReload.hidden = true;
    netExit.textContent = t("Salir al menú");
    disconnectOverlay.classList.add("is-hidden");
  };
  netExit.addEventListener("click", () => {
    if (!netHere.hidden) elsewhereDismissed = true;
    if (!netReload.hidden) outdatedDismissed = true;
    if (elsewhereCard) {
      hideElsewhereCard();
      return;
    }
    void openHome();
  });
  // Recuperar la conexion aqui: el servidor se queda con esta y cierra la otra,
  // que se para (no vuelve sola), asi que no hay pelea.
  netHere.addEventListener("click", () => {
    netHere.disabled = true;
    void onlineClient.connect().catch(() => {});
  });

  const unlockMenuMusic = (): void => {
    menuMusic.play();
    window.removeEventListener("keydown", unlockMenuMusic);
    window.removeEventListener("pointerdown", unlockMenuMusic);
  };
  window.addEventListener("keydown", unlockMenuMusic);
  window.addEventListener("pointerdown", unlockMenuMusic);

  const app = new Application();
  await app.init({
    resizeTo: mount,
    background: COLORS.BACKGROUND,
    antialias: true,
    autoStart: false,
    resolution: Math.min(window.devicePixelRatio, 2),
    autoDensity: true,
  });

  mount.appendChild(app.canvas);
  mount.classList.add("is-menu");

  /*
   * Calidad que se ajusta sola, y el chivato que lo hace visible.
   *
   * Van juntos porque responden a la misma queja —"en el portatil laguea"— por
   * los dos lados: uno intenta arreglarlo y el otro dice si el problema era
   * de verdad la red o era el ordenador. Sin el segundo, la unica forma de
   * saberlo es la sensacion de quien juega, y la sensacion no distingue entre
   * 200 ms de ping y 25 fps.
   */
  const quality = createAdaptiveQuality(app);
  const netStatus = createNetStatus(document.body);
  /** Coste del frame anterior, para alimentar el ajuste de calidad. */
  let lastFrameAt = 0;
  /** El partido ha divergido. Lo pinta el chivato en rojo hasta salir. */
  let desynced = false;

  // Handle que busca la extension Pixi DevTools. Solo en desarrollo: en
  // produccion no se cuelga nada del objeto global.
  if (import.meta.env.DEV) {
    (globalThis as { __PIXI_APP__?: Application }).__PIXI_APP__ = app;
  }

  // Antes del primer partido: `createField` es sincrono y si las texturas
  // llegaran tarde el campo ya estaria construido sin ellas. Si fallan, no
  // pasa nada: cada capa se dibuja en vectorial como siempre.
  await preloadTextures();

  /*
   * Ya hay algo que enseñar: se abre el velo. Antes de esta linea la pantalla
   * es el fondo pelado, y ese es justo el hueco que el velo tapa — al arrancar
   * y al recargar por cambio de idioma.
   */
  revealApp();

  let match: Match | null = null;
  let loop: Loop | null = null;
  let lastWidth = -1;
  let lastHeight = -1;

  /*
   * El encuadre se recalcula cuando cambia el TAMAÑO, y cambiar de modo de
   * camara no cambia el tamaño: sin esto se elegia "CERCA" y no pasaba nada
   * hasta girar el movil. Invalidar la medida cacheada es todo lo que hace
   * falta — el propio bucle de dibujo la vuelve a pedir en el frame siguiente.
   */
  onCameraModeChange(() => {
    lastWidth = -1;
    lastHeight = -1;
  });

  /*
   * Entrar y salir de pantalla completa: volver a medir, y no una sola vez.
   *
   * `resizeTo` de Pixi escucha el `resize` de la ventana, pero al entrar en
   * pantalla completa ese evento llega ANTES de que el navegador haya terminado
   * de recolocar la caja: Pixi se queda con la medida vieja y el juego se
   * dibuja pequeño, con franja negra, hasta el siguiente cambio de tamaño. Se
   * vuelve a medir en los dos frames siguientes, que es cuando la caja ya es la
   * definitiva en los navegadores donde falla (Chrome de Android y Safari de
   * iPad).
   */
  onFullscreenChange(() => {
    const remeasure = (): void => {
      app.resize();
      lastWidth = -1;
      lastHeight = -1;
    };
    remeasure();
    requestAnimationFrame(remeasure);
    requestAnimationFrame(() => requestAnimationFrame(remeasure));
  });
  let ending = false;
  let navigating = false;
  let activeLockstep: ReturnType<typeof createLockstep> | null = null;
  let lastMatchOptions: MatchOptions = { shirtNumber: 10, assist: "normal", pitchSkin: DEFAULT_PITCH_SKIN };
  let lastModeId: MatchModeId = "3v3";
  let recoverableOverlay = true;
  let clipPlayer: ClipPlayerHandle | null = null;
  let resultCard: ResultCardHandle | null = null;
  let matchChat: MatchChatHandle | null = null;
  let loopPausedByVisibility = false;
  /**
   * El partido de carrera en juego: lo que se monto y como deshacerlo. Vive
   * hasta volver al menu (la pantalla de resultado aun enseña los dos clubes).
   */
  let careerRun: { setup: CareerMatchSetup; restore: () => void } | null = null;

  /*
   * El encargo del mister, en pantalla durante el partido de carrera: como el
   * objetivo de una mision de GTA, con lo que llevas. Se refresca dos veces
   * por segundo (el resumen de estadisticas se recalcula entero: no a 60 Hz).
   */
  const objectivePill = document.createElement("div");
  objectivePill.className = "career-objective is-hidden";
  objectivePill.setAttribute("role", "status");
  document.body.appendChild(objectivePill);
  let objectiveShown: Objective | null = null;
  let objectiveCheckAt = 0;
  const hideObjective = (): void => {
    objectiveShown = null;
    objectivePill.classList.add("is-hidden");
  };
  const updateObjective = (now: number, force = false): void => {
    const o = objectiveShown;
    if (!o || !match || !careerRun) return;
    if (!force && now - objectiveCheckAt < 500) return;
    objectiveCheckAt = now;
    const me = match.stats().players.find((p) => p.id === match!.humanId);
    const side = careerRun.setup.side;
    const goalsFor = match.state.score[side];
    const goalsAgainst = match.state.score[side === "home" ? "away" : "home"];
    const progress = objectiveProgress(o, {
      goals: me?.goals ?? 0,
      shots: me?.shots ?? 0,
      assists: me?.assists ?? 0,
      passes: me?.passes ?? 0,
      passesOk: me?.passesOk ?? 0,
      recoveries: me?.recoveries ?? 0,
      goalsFor,
      goalsAgainst,
    });
    let count: string;
    if (o.kind === "passes") count = me && me.passes > 0 ? `${progress.value} %` : "—";
    else if (o.kind === "cleanSheet") count = t("{n} encajados", { n: goalsAgainst });
    else if (o.kind === "win") count = goalsFor > goalsAgainst ? t("ganando") : goalsFor < goalsAgainst ? t("perdiendo") : t("empate");
    else count = `${Math.min(progress.value, o.target)}/${o.target}`;
    objectivePill.classList.toggle("is-done", progress.done);
    objectivePill.innerHTML = `<span aria-hidden="true">${progress.done ? "✓" : "🎯"}</span><span>${escapeHtml(
      t(objectiveKey(o), { n: o.target }),
    )}</span><b>${escapeHtml(count)}</b>`;
  };
  /** La pregunta de "si te vas, se juega sin ti" esta abierta. */
  let careerLeaveOpen = false;

  onlineClient.onState((state) => {
    netHere.disabled = state === "connecting" || state === "reconnecting";
    if (state !== "elsewhere") elsewhereDismissed = false;
    if (state !== "outdated") outdatedDismissed = false;
    if (state === "elsewhere") {
      showElsewhere();
      return;
    }
    if (state === "outdated") {
      showOutdated();
      return;
    }
    if (!match || ending) {
      hideElsewhereCard();
      return;
    }
    if (state === "online") hideDisconnect();
    if (state === "offline" && activeLockstep) showDisconnect();
  });

  const netTitle = disconnectOverlay.querySelector("[data-net-title]");
  const netBody = disconnectOverlay.querySelector("[data-net-body]");

  const showDisconnect = (
    title = t("Conexión perdida"),
    body = t("Esperando reconexión con el servidor…"),
    recoverable = true,
  ): void => {
    loop?.stop();
    match?.suspend();
    elsewhereCard = false;
    netHere.hidden = true;
    netReload.hidden = true;
    netExit.textContent = t("Salir al menú");
    if (netTitle) netTitle.textContent = title;
    if (netBody) netBody.textContent = body;
    // Un desync no se arregla solo: si volviera el loop, cada jugador seguiria
    // viendo un partido distinto. Solo se reanuda lo que puede recuperarse.
    recoverableOverlay = recoverable;
    disconnectOverlay.classList.remove("is-hidden");
  };

  /**
   * La cuenta se ha abierto en otra pestaña o dispositivo. En un partido online
   * es un corte como otro cualquiera (se para y se puede volver); fuera, un
   * aviso que no bloquea nada que funcione sin conexion.
   */
  const showElsewhere = (force = false): void => {
    if (onlineClient.state !== "elsewhere") return;
    const title = t("Abierto en otra pestaña");
    const body = t(
      "Tu cuenta se está usando en otra pestaña o en otro dispositivo, y la conexión se ha quedado allí. Pulsa «Jugar aquí» para traerla.",
    );
    if (match && !ending && activeLockstep) {
      showDisconnect(title, body, true);
      netHere.hidden = false;
      return;
    }
    // Un partido sin red (Local / IA, la carrera) o la pantalla final no se
    // tapan: sale al volver al menu.
    if (match) return;
    if (elsewhereDismissed && !force) return;
    if (netTitle) netTitle.textContent = title;
    if (netBody) netBody.textContent = body;
    elsewhereCard = true;
    netHere.hidden = false;
    netExit.textContent = t("Seguir sin conexión");
    disconnectOverlay.classList.remove("is-hidden");
  };

  /**
   * Este cliente y el servidor traen simulaciones distintas (`CLOSE_OUTDATED`):
   * casi siempre, una pestaña abierta de antes de un despliegue. Como la de
   * "otra pestaña": en un partido online lo para; fuera, avisa sin bloquear lo
   * que funciona sin conexion, y nunca tapa un partido sin red en curso.
   */
  const showOutdated = (force = false): void => {
    if (onlineClient.state !== "outdated") return;
    const title = t("Hay una versión nueva de FYNTA");
    const body = t(
      "Recarga la página para jugar online con los demás. Local / IA y la carrera siguen funcionando sin recargar.",
    );
    if (match && !ending && activeLockstep) {
      showDisconnect(title, body, false);
      netReload.hidden = false;
      return;
    }
    if (match) return;
    if (outdatedDismissed && !force) return;
    if (netTitle) netTitle.textContent = title;
    if (netBody) netBody.textContent = body;
    elsewhereCard = true;
    netHere.hidden = true;
    netReload.hidden = false;
    netExit.textContent = t("Seguir sin conexión");
    disconnectOverlay.classList.remove("is-hidden");
  };

  /*
   * RETRANSMISION: rotulos de los cambios de plan de la IA y consejos de la
   * primera vez con el cuerpo. Ver `game/commentary.ts` y `ui/matchCaption.ts`.
   */
  const matchCaption = createMatchCaption(document.body);
  let commentary: ReturnType<typeof createCommentary> | null = null;
  let captionClock = 0;

  const captionFor = (event: CaptionEvent): CaptionContent | null => {
    if (!match) return null;
    const humanSide = match.players.find((p) => p.id === match!.humanId)?.side ?? "home";
    const mine = event.side === humanSide;
    const team = TEAM_IDENTITY[event.side].name;
    if (event.kind === "style") {
      const style = careerRun?.setup.teams[event.side].style;
      if (!style) return null;
      return {
        kicker: team,
        title: t("Juegan: {style}", { style: t(STYLE_LABEL[style]) }),
        sub: t(STYLE_BLURB[style]),
        tone: "style",
      };
    }
    if (event.kind === "chase") {
      return mine
        ? {
            kicker: t("TU EQUIPO"),
            title: t("Los tuyos se vuelcan"),
            sub: t("Aprietan arriba y van a por el gol."),
            tone: "chase",
          }
        : {
            kicker: team,
            title: t("Se vuelcan al ataque"),
            sub: t("Arriesgan con todo: detrás dejan sitio para la contra."),
            tone: "chase",
          };
    }
    return mine
      ? {
          kicker: t("TU EQUIPO"),
          title: t("Los tuyos cierran el partido"),
          sub: t("Bloque bajo y sin prisa: toca aguantar."),
          tone: "protect",
        }
      : {
          kicker: team,
          title: t("Se encierran atrás"),
          sub: t("Cierran el partido y su portero se toma su tiempo."),
          tone: "protect",
        };
  };

  /** Una vez por fotograma: lo que la IA ha decidido y todavia no se ha contado. */
  const narrate = (now: number): void => {
    if (!commentary || !match || ending) return;
    const dt = captionClock > 0 ? Math.min(0.25, (now - captionClock) / 1000) : 0;
    captionClock = now;
    const events = commentary.update({
      state: match.state,
      urgency: { home: match.aiWorld.tactics.home.urgency, away: match.aiWorld.tactics.away.urgency },
      dt,
    });
    for (const event of events) {
      const content = captionFor(event);
      if (content) matchCaption.show(content);
    }
  };

  /**
   * La O, con el nombre que tiene en TU mando: la tecla en teclado, el boton
   * «Correr» en tactil, X en un mando.
   */
  const bodyKey = (): string => {
    if (wantsTouchControls()) return t("«Correr»");
    const pads = typeof navigator.getGamepads === "function" ? navigator.getGamepads() : [];
    // Solo un mando de verdad: un pedal o una palanca USB tambien sale en la lista.
    return [...pads].some((pad) => pad?.connected && pad.mapping === "standard") ? "X" : primaryKey("dash");
  };

  /** Consejos que ya se han visto en este navegador: cada uno sale una vez. */
  const TIPS_KEY = "fynta.tips.seen";
  const tipSeen = (id: string): boolean => {
    try {
      const seen = JSON.parse(localStorage.getItem(TIPS_KEY) ?? "[]") as unknown;
      return Array.isArray(seen) && seen.includes(id);
    } catch {
      return false;
    }
  };
  const markTip = (id: string): void => {
    try {
      const seen = JSON.parse(localStorage.getItem(TIPS_KEY) ?? "[]") as unknown;
      const list = Array.isArray(seen) ? seen : [];
      if (!list.includes(id)) localStorage.setItem(TIPS_KEY, JSON.stringify([...list, id]));
    } catch {
      // Sin almacenamiento, el consejo puede repetirse: mejor eso que no avisar.
    }
  };
  const tip = (id: string, title: string, sub: string): void => {
    if (tipSeen(id)) return;
    markTip(id);
    matchCaption.show({ kicker: t("CONSEJO"), title, sub, tone: "tip" });
  };

  /*
   * El cuerpo (la O) tiene una ventana de dos decimas y nadie la descubre solo.
   * Se cuenta la primera vez que pasa algo de cuerpo con TU jugador, que es
   * justo cuando la explicacion tiene sentido.
   */
  const bodyContactTip = (kind: "check" | "block", byId: string, victimId: string): void => {
    const me = match?.humanId;
    if (!me) return;
    if (kind === "check" && victimId === me) {
      tip(
        "body-checked",
        t("Te han embestido"),
        t("Pulsa {key} justo antes del choque para plantarte: rebota él y el balón sigue siendo tuyo.", {
          key: bodyKey(),
        }),
      );
    } else if (kind === "block" && victimId === me) {
      tip(
        "body-braced",
        t("¡Te has plantado!"),
        t("Así se aguanta un balón: {key} justo antes del choque, y rebota el que embiste.", { key: bodyKey() }),
      );
    } else if (kind === "block" && byId === me) {
      tip(
        "body-bounced",
        t("Se ha plantado"),
        t("Te ha visto venir y has rebotado tú. Embiste cuando no te espere, no cuando te está mirando."),
      );
    }
  };

  /*
   * El aftertouch (`KICK.AFTERTOUCH_*`) no se descubre solo: se cuenta tras tu
   * primer disparo, que es cuando tiene sentido.
   */
  const shotTip = (): void =>
    tip(
      "aftertouch",
      t("Coloca el tiro"),
      wantsTouchControls()
        ? t("Justo después de chutar, lleva el joystick hacia un lado y el balón se dobla hacia allí.")
        : t("Justo después de chutar, mantén una dirección hacia un lado y el balón se dobla hacia allí."),
    );

  const showDesync = (tick: number): void => {
    console.error(`[golazo] desync detectado en el tick ${tick}`);
    desynced = true;
    showDisconnect(
      t("Partida desincronizada"),
      t("Los jugadores han dejado de ver el mismo partido. Hay que volver al menú."),
      false,
    );
  };

  const hideDisconnect = (): void => {
    if (!recoverableOverlay) return;
    disconnectOverlay.classList.add("is-hidden");
    if (match && !ending) {
      match.resume();
      loop?.start();
    }
  };

  /** Los controles en pantalla durante el partido (ver `ui/controlsStrip.ts`). */
  let controlsStrip: ControlsStrip | null = null;

  const clearMatch = (): void => {
    matchCaption.clear();
    controlsStrip?.dispose();
    controlsStrip = null;
    commentary = null;
    // Las reglas de la jornada nunca sobreviven al partido que las traia.
    clearWeeklyRule();
    hideObjective();
    clipPlayer?.dispose();
    clipPlayer = null;
    resultCard?.dispose();
    resultCard = null;
    matchChat?.dispose();
    matchChat = null;
    loop?.stop();
    loopPausedByVisibility = false;
    loop = null;
    recoverableOverlay = true;
    desynced = false;
    lastFrameAt = 0;
    netStatus.hide();
    disconnectOverlay.classList.add("is-hidden");
    elsewhereCard = false;
    netHere.hidden = true;
    netReload.hidden = true;
    onlineClient.activeRoomId = null;
    activeLockstep?.dispose();
    activeLockstep = null;
    if (match) {
      app.stage.removeChild(match.root);
      match.dispose();
      match = null;
    }
    lastWidth = -1;
    lastHeight = -1;
    ending = false;
    hideDisconnect();
    chrome.hideResult();
    chrome.hideExit();
    app.renderer.clear({ clearColor: COLORS.BACKGROUND });
  };

  const startMatch = (opts: MatchOptions): void => {
    clearMatch();
    activeLockstep = opts.network?.lockstep ?? null;

    /*
     * Que se vea lo que hace la IA (rotulos de retransmision y consejos de la
     * primera vez). Solo contra la IA y con un jugador: en online deciden
     * personas, y con dos en el mismo ordenador el 1 corre con Shift, no con la
     * O que nombran los consejos.
     */
    const narrated = !opts.network && !opts.secondPlayerId;
    commentary = narrated
      ? createCommentary({ styleSide: careerRun ? (careerRun.setup.side === "home" ? "away" : "home") : undefined })
      : null;
    captionClock = 0;
    if (narrated) opts = { ...opts, onBodyContact: bodyContactTip, onHumanShot: shotTip };

    /*
     * Los controles en pantalla: los primeros partidos y siempre contra el
     * Barrio (nivel 0 o menos: el Partido rapido en Barrio y el barrio de la
     * carrera). No con dos en el mismo teclado —cada uno tiene su mitad y
     * sus teclas fijas— ni en tactil, donde los controles son los botones.
     */
    const barrio = opts.aiSkill !== undefined && Math.min(opts.aiSkill.home ?? 1, opts.aiSkill.away ?? 1) <= 0;
    if (!opts.secondPlayerId && wantsControlsStrip(barrio)) {
      // En tactil los controles son los botones: en vez de la tira, un pulgar
      // fantasma que enseña cada gesto en su sitio (`ui/touchCoach.ts`).
      if (wantsTouchControls()) opts = { ...opts, touchCoach: true };
      else controlsStrip = createControlsStrip(document.body);
    }

    /*
     * ONLINE: instrucciones de fabrica, iguales en las dos maquinas.
     *
     * El plan de equipo (pestaña PLAN de personalizar) muta `TEAM_INSTRUCTIONS`
     * en sitio, y ese objeto lo lee la IA DENTRO de la simulacion. En un
     * partido en red eso significa que el plan que cada uno tuviera guardado de
     * su ultima partida local entraba en la simulacion compartida: mismo tick,
     * mismas entradas, decisiones distintas. Desincronizacion garantizada, y de
     * las que no se ven venir porque el culpable es un ajuste de menu y no el
     * codigo de red.
     *
     * En local no se toca: ahi el plan es tuyo y no hay con quien cuadrar.
     */
    if (activeLockstep) {
      resetTeamInstructions();
      /*
       * Y la duracion, por lo mismo: `MATCH.DURATION` arranca `timeRemaining`,
       * asi que dos duraciones distintas terminan el partido en ticks
       * distintos. Se borra la preferencia local y manda LA SALA, que es la
       * unica fuente que las dos maquinas comparten.
       */
      /*
       * El formato lo dicta LA SALA, entero: minutos, tiempos y que pasa si
       * empatan. Se resetea a fabrica primero para que lo que la sala no diga
       * quede en el valor de siempre y no en la preferencia local de cada uno.
       */
      resetMatchFormat();
      if (opts.roomFormat) setMatchFormat(opts.roomFormat);
    }
    mount.classList.remove("is-menu");
    // Al campo se entra sin musica: la del menu se calla y no entra otra.
    menuMusic.stop();
    /*
     * Pantalla completa al empezar a jugar, en movil.
     *
     * Se pide AQUI y no al pulsar el boton del menu porque este es el ultimo
     * punto de la cadena que todavia esta dentro de la ventana de activacion
     * que dejo ese clic: los navegadores rechazan `requestFullscreen` fuera de
     * un gesto, y el menu resuelve por promesa, no en el propio manejador.
     *
     * Solo en movil de mano y solo si no se ha dicho que no antes. En
     * escritorio la barra del navegador no le esta quitando sitio al campo, y
     * secuestrar la pantalla de alguien que no lo ha pedido es de mala
     * educacion. `prefersFullscreen()` recuerda el "no" explicito.
     */
    if (isMobileHandheld() && supportsFullscreen() && prefersFullscreen()) {
      void enterFullscreen();
    } else {
      void mobileShell.tryLockLandscape();
    }
    lastMatchOptions = opts;

    /*
     * El gesto de "atras" no puede tirar el partido.
     *
     * En Android, deslizar desde el borde durante un partido cerraba la pestaña
     * entera: se perdia la partida y, si era online, se dejaba tirados a los
     * demas. Con una entrada de historial propia, ese gesto consume ESTA
     * entrada y lo unico que hace es volver al menu, que es lo que la persona
     * queria de verdad. Pantalla completa ayuda (oculta la barra de navegacion
     * en Android) pero no cubre el deslizamiento desde el borde.
     */
    try {
      history.pushState({ fyntaMatch: true }, "");
    } catch {
      // Historial bloqueado (algun navegador embebido): se juega igual, solo
      // que el gesto de atras vuelve a hacer lo de siempre.
    }

    match = createMatch(opts);
    app.stage.addChild(match.root);
    // El chivato solo en online: sin nadie al otro lado no hay ping que medir, y
    // un contador de fps permanente en un partido contra la IA es ruido.
    if (activeLockstep) netStatus.show();
    // El chat de la sala sigue abierto dentro del partido, como en HaxBall.
    if (activeLockstep) {
      matchChat = createMatchChat(document.body, {
        chat: roomChat,
        selfId: () => onlineClient.clientId,
        touch: window.matchMedia?.("(pointer: coarse)").matches ?? false,
        room: matchRoomControls,
      });
    }
    chrome.showExit();
    app.canvas.tabIndex = 0;
    app.canvas.focus({ preventScroll: true });

    const syncViewport = (): void => {
      if (!match) return;
      const { width, height } = app.screen;
      if (width === lastWidth && height === lastHeight) return;
      lastWidth = width;
      lastHeight = height;
      match.resize(width, height);
      matchCaption.setBottom(scoreBugClearance(isCompact(width, height)) + 8);
    };
    syncViewport();

    loop = createLoop({
      tick: (dt) => match?.tick(dt),
      render: (alpha) => {
        if (!match) return;
        // Se mide el frame ANTERIOR completo (dibujo incluido), no solo esta
        // funcion: lo que decide si el juego se ve fluido es cuanto tarda el
        // ciclo entero, y el dibujo de Pixi es justo la parte cara.
        const now = performance.now();
        if (lastFrameAt > 0) quality.sample(now - lastFrameAt);
        lastFrameAt = now;

        syncViewport();
        updateObjective(now);
        narrate(now);
        match.render(alpha);
        app.render();
        if (activeLockstep) {
          netStatus.update({
            rttMs: onlineClient.rtt,
            frameMs: quality.frameMs,
            backlog: activeLockstep.backlog,
            lead: activeLockstep.lead,
            desynced,
            replaying: replayProgress(match.state) > 0,
          });
        }
        if (match.state.phase === "ended" && !ending) {
          void handleMatchEnd();
        }
      },
    });
    loop.start();

    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__golazo = {
        app,
        match,
        loop,
        onlineClient,
        tune: {
          PLAYER,
          BALL,
          POSSESSION,
          KICK,
          AI,
          AIM,
          GK,
          TEAM_INSTRUCTIONS,
          TACTICS,
          MATCH_MODES,
          ACTIVE_MATCH_MODE,
        },
        createAiController,
        createMatch,
      };
    }
  };

  /** Deja el Partido rapido como estaba: equipos, instrucciones y formato. */
  const endCareerRun = (): void => {
    careerRun?.restore();
    careerRun = null;
  };

  /**
   * Juega el siguiente partido de la carrera. `false` si no hay partido que
   * jugar (no hay carrera, fin de temporada, retirado).
   *
   * Antes de montarlo se apunta como empezado: si el juego se cierra a mitad,
   * al volver se da por jugado sin ti. El resultado es el resultado.
   */
  const playCareerMatch = (): boolean => {
    let career = loadCareer();
    if (!career) return false;
    const leftover = resolvePending(career);
    if (leftover) {
      career = leftover.career;
      claimCareerUnlocks(unlockStore(), career);
      saveCareer(career);
    }
    const setup = careerMatchSetup(career);
    if (!setup) return false;
    clearMatch();
    endCareerRun();
    saveCareer(beginMatch(career));
    careerRun = { setup, restore: applyCareerMatch(setup) };
    lastModeId = setup.modeId;
    mount.classList.remove("is-menu");
    startMatch(careerMatchOptions(setup));
    const objective = career.objective;
    if (objective && objective.season === setup.season && objective.matchday === setup.matchday && !objective.result) {
      objectiveShown = objective;
      objectivePill.classList.remove("is-hidden");
      updateObjective(performance.now(), true);
    }
    return true;
  };

  /**
   * Apunta en la carrera el partido que acaba de terminar y dice que enseñar.
   * Solo si el partido empezado es este: si otra pestaña lo apunto antes, no
   * se apunta dos veces.
   */
  const recordCareerMatch = (
    setup: CareerMatchSetup,
    outcome: ReturnType<typeof summariseMatch>,
  ): string | null => {
    const saved = loadCareer();
    if (!saved?.pending || saved.pending.season !== setup.season || saved.pending.matchday !== setup.matchday) {
      return null;
    }
    const firstNew = saved.nextMessageId;
    const step = recordMatch(
      saved,
      performanceOf({
        goalsFor: outcome.goalsFor,
        goalsAgainst: outcome.goalsAgainst,
        isMvp: outcome.isMvp,
        stat: outcome.stat,
      }),
    );
    saveCareer(step.career);
    const unlocked = claimCareerUnlocks(unlockStore(), step.career);
    return careerResultHtml({
      career: step.career,
      events: step.events,
      messages: step.career.feed.filter((m) => m.id >= firstNew),
      unlocked,
    });
  };

  /** Salir a mitad de un partido de carrera: se pregunta, y si te vas, se juega sin ti. */
  const leaveCareerMatch = async (): Promise<void> => {
    if (careerLeaveOpen) return;
    careerLeaveOpen = true;
    loop?.stop();
    match?.suspend();
    const leave = await confirmDialog({
      title: t("¿Te vas del partido?"),
      body: t("Si te vas, se juega sin ti y el resultado cuenta. Aquí no se repite nada."),
      confirm: t("Me voy"),
      cancel: t("Sigo jugando"),
    });
    careerLeaveOpen = false;
    if (!leave) {
      if (match && !ending) {
        match.resume();
        lastFrameAt = 0;
        loop?.start();
        // El gesto de atras se comio la entrada del partido: se repone.
        try {
          if (!(history.state as { fyntaMatch?: boolean } | null)?.fyntaMatch) {
            history.pushState({ fyntaMatch: true }, "");
          }
        } catch {
          // Sin historial, el siguiente "atras" hace lo de siempre.
        }
      }
      return;
    }
    const saved = loadCareer();
    const step = saved ? resolvePending(saved) : null;
    if (step) {
      saveCareer(step.career);
      claimCareerUnlocks(unlockStore(), step.career);
    }
    await openHome();
  };

  /**
   * Sala de un enlace de invitacion. Se consume una sola vez: si el jugador
   * vuelve al menu, no queremos reenviarlo a la misma sala una y otra vez.
   */
  let pendingInvite = parseInviteRoom();
  /** Las personas del ultimo partido online, para "Has jugado con". */
  let onlineMates: Mate[] = [];
  let unmountMates: (() => void) | null = null;
  /*
   * Enlace con sala Y codigo: en cuanto estes dentro de esa sala, se pide la
   * amistad "por el enlace" y el servidor os hace amigos sin esperar (ver
   * `friends:add` con `viaRoom`). Quien te paso el enlace ya dijo que si.
   */
  let pendingInviter = pendingInvite ? parseInviteFrom() : null;
  if (pendingInviter && pendingInvite) {
    const roomId = pendingInvite;
    const code = pendingInviter;
    const off = onlineClient.onMessage((msg) => {
      if (msg.type !== "room:update" || msg.room.id !== roomId || !pendingInviter) return;
      const me = onlineClient.clientId;
      const inside =
        Object.values(msg.room.seats).some((o) => o?.clientId === me) || (msg.room.bench ?? []).some((b) => b.clientId === me);
      if (!inside) return;
      pendingInviter = null;
      off();
      // Tu propio enlace: nada que hacer.
      if (friendsStore.state()?.code !== code) friendsStore.addViaLink(code, roomId);
    });
  }
  /** Destino pedido desde fuera del bucle del menu (ver `goToFriend`). */
  let queuedHome: HomeTarget | null = null;

  /**
   * A donde volver al salir de un partido online.
   *
   * La sala ya no muere con el partido: al acabar, o cuando el anfitrion te
   * saca al banquillo o para el partido, se vuelve A LA SALA, no al menu. Y si
   * te echan, a la lista de salas con el aviso, no a un menu sin explicacion.
   */
  interface HomeTarget {
    roomId?: string;
    lobby?: boolean;
    notice?: string;
    /** Abrir la conversacion con este amigo. */
    dm?: string;
    /** Entrar en esta sala (la de un amigo). */
    joinRoomId?: string;
    /** Montar una sala para amigos e invitar a este. */
    playWith?: string;
    /** Sala para amigos nueva con el enlace a la vista, para pasarlo fuera. */
    inviteNew?: boolean;
    /** Abrir la carrera (al volver de un partido de carrera, o por `?carrera`). */
    career?: boolean;
    /** ...y en la pantalla de crear jugador. */
    careerCreate?: boolean;
  }

  /**
   * Lo offline: la pantalla de "Local / IA" y, desde ella, la carrera. Devuelve
   * `true` si ha empezado un partido. Salir de la carrera vuelve a "Local /
   * IA", que es de donde se entra; salir de ahi, al menu principal.
   */
  const offlineFlow = async (startInCareer = false, create = false): Promise<boolean> => {
    let inCareer = startInCareer;
    while (true) {
      if (inCareer) {
        const action = await careerHub.show({ create });
        create = false;
        if (action.type === "play" && playCareerMatch()) return true;
        inCareer = false;
        // Se salio por un aviso de un amigo: al lobby, no a "Local / IA".
        if (queuedHome) return false;
      }
      const pick = await privateMenu.show();
      if ("back" in pick) return false;
      if ("career" in pick) {
        inCareer = true;
        continue;
      }
      lastModeId = pick.modeId;
      // Dos en el mismo ordenador: los dos asientos son de personas.
      // Solo en el navegador con teclado; el menu ya lo esconde, esto es por si acaso.
      const secondId = localPairAvailable() ? secondSeat(pick.modeId, pick.players) : null;
      startMatch({
        shirtNumber: pick.shirtNumber,
        assist: pick.assist as AssistLevel,
        pitchSkin: pick.pitchSkin,
        nickname: pick.nickname,
        aiSkill: difficultySkill(pick.difficulty),
        ...(secondId
          ? {
              secondPlayerId: secondId,
              humanSeats: [
                { playerId: firstSeat(pick.modeId), nickname: pick.nickname || t("Jugador 1") },
                { playerId: secondId, nickname: t("Jugador 2") },
              ],
            }
          : {}),
      });
      return true;
    }
  };

  const openHome = async (target: HomeTarget = {}): Promise<void> => {
    if (navigating) return;
    navigating = true;
    try {
      clearMatch();
      endCareerRun();
      mount.classList.add("is-menu");
      menuMusic.play();
      privateMenu.hide();
      onlineLobby.hide();

      if (onlineClient.state === "offline") {
        try {
          await onlineClient.connect();
        } catch {
          // Lobby shows offline state; user can retry by re-entering.
        }
      }
      showElsewhere();
      showOutdated();

      // Despues de conectar, para que la opcion de Google pueda ofrecerse: el
      // Client ID llega en `welcome`. Sin server se entra igual de invitado.
      if (!hasEntered()) {
        await entryGate.show();
        markEntered();
      }
      entryGate.hide();
      if (pendingNicknameNotice) {
        const notice = pendingNicknameNotice;
        pendingNicknameNotice = null;
        void nicknameDialog.open(notice);
      }

      while (true) {
        mainMenu.hide();
        onlineLobby.hide();
        privateMenu.hide();

        // Con un enlace en la URL no se ensena el menu: se va derecho a la sala.
        // Igual al volver de un partido online: a la sala, o a la lista.
        const invite = pendingInvite;
        pendingInvite = null;
        // Un destino pedido desde dentro de otra pantalla (la ficha de un
        // amigo abierta en "Mis estadisticas") manda sobre el de la llamada.
        const back = queuedHome ?? target;
        queuedHome = null;
        target = {};
        if (back.career) {
          if (await offlineFlow(true, back.careerCreate)) return;
          continue;
        }
        const toOnline = Boolean(invite || back.roomId || back.lobby || back.dm || back.joinRoomId || back.playWith || back.inviteNew);
        const choice = toOnline ? "online" : await mainMenu.show();

        // Se salio del menu por un aviso de un amigo: el destino ya esta puesto.
        if (choice === "again") continue;

        if (choice === "relang") {
          // Idioma nuevo: se rehace la interfaz y se vuelve a enseñar el menu.
          rebuildMenus();
          continue;
        }

        if (choice === "world") {
          const action = await worldScreen.show();
          // "Jugar online" desde ahi: al lobby, que es donde se suben puestos.
          if (action.type === "lobby") target = { lobby: true };
          continue;
        }

        if (choice === "career") {
          await careerScreen.show({ online: profileBook.online.get(), offline: profileBook.offline.get() });
          continue;
        }

        if (choice === "private") {
          if (await offlineFlow()) return;
          continue;
        }

        // La jornada ya no entra por aqui: se elige dentro del lobby.
        // Y si la conexion la tiene otra pestaña, lo primero es decirlo.
        showElsewhere(true);
        showOutdated(true);
        const result = await onlineLobby.show({
          joinRoomId: invite ?? back.joinRoomId,
          roomId: back.roomId,
          notice: back.notice,
          dm: back.dm,
          playWith: back.playWith,
          inviteNew: back.inviteNew,
        });
        if ("back" in result) continue;

        setMatchMode(result.room.modeId);
        lastModeId = result.room.modeId;
        // La jornada la dice el server en `match:start`; aqui solo se aplica.
        const weeklyRule = ruleById(result.weeklyRuleId);
        if (weeklyRule) applyWeeklyRule(weeklyRule);
        const humanSeats = Object.entries(result.room.seats)
          .filter((entry): entry is [string, { clientId: string; nickname: string }] =>
            Boolean(entry[1]),
          )
          .map(([playerId, occ]) => ({
            playerId,
            nickname: occ.nickname,
          }));
        // Con quien juegas hoy: sale en Amigos ("Jugaste con") para añadirle.
        rememberPlayers(
          Object.values(result.room.seats).filter((o): o is NonNullable<typeof o> => Boolean(o)),
          onlineClient.clientId,
        );
        // Y en los resultados, con su boton de añadir (ver `matesStrip.ts`).
        const mySide = result.localPlayerId.startsWith("away") ? "away" : "home";
        onlineMates = Object.entries(result.room.seats)
          .filter((e): e is [string, { clientId: string; nickname: string }] => Boolean(e[1]) && e[1]!.clientId !== onlineClient.clientId)
          .map(([seatId, occ]) => ({ clientId: occ.clientId, nickname: occ.nickname, teammate: seatId.startsWith(mySide) }));

        const matchOpts: MatchOptions = {
          seed: result.seed,
          assist: "normal",
          humanSeats,
          localPlayerId: result.localPlayerId,
          /*
           * El campo lo pone LA SALA, no la preferencia de cada uno.
           *
           * Es lo unico del aspecto que tiene que ser compartido: si cada
           * cliente pintase su cesped, dos jugadores del mismo partido estarian
           * hablando de sitios distintos. La camiseta si es de cada cual —esa no
           * cambia de que se esta hablando— pero el escenario es uno.
           */
          pitchSkin: result.room.fieldId,
          // El formato lo pone la SALA, no la preferencia de cada uno.
          roomFormat: {
            minutes: result.room.minutes,
            periods: result.room.periods,
            onDraw: result.room.onDraw,
          },
        };

        if (result.client) {
          const lockstep = createLockstep({
            client: result.client,
            roomId: result.roomId,
            localPlayerId: result.localPlayerId,
            seed: result.seed,
            solo: result.solo === true,
            history: result.lateJoinHistory as
              | Array<{ tick: number; frames: Record<string, WireInputFrame> }>
              | undefined,
          });
          lockstep.onDesync(showDesync);
          lockstep.onMatchOver((reason) => {
            if (reason === "stopped") {
              // Lo ha parado el anfitrion: no es un final, no cuenta para la
              // carrera. Todos vuelven a la sala.
              if (!ending) void backToRoom(t("El anfitrión ha parado el partido"));
              return;
            }
            if (reason === "abandoned") {
              showDisconnect(
                t("Partida abandonada"),
                t("No queda nadie conectado en la sala."),
                false,
              );
            } else {
              void handleMatchEnd();
            }
          });
          matchOpts.network = {
            lockstep,
            onDisconnect: () => showDisconnect(),
          };
        }

        // Si el server se reinicia, este id le dice al reconectar que partida
        // creiamos estar jugando, y nos contesta si ya no existe.
        result.client?.send({ type: "match:ready", roomId: result.roomId });
        startMatch(matchOpts);
        // DESPUES de `startMatch`, no antes: `startMatch` empieza por
        // `clearMatch`, que la pone a null. Apuntada antes, el partido entero
        // corria sin saber en que sala estaba: ni el aviso de fin, ni volver a
        // la sala, ni salir de ella, ni el `hello` al reconectar la veian.
        onlineClient.activeRoomId = result.roomId;
        liveRoom = result.room;
        // `match:start` no trae el banquillo; la lista si (para tu sala). El
        // `room:update` que lo traia llego antes de apuntar la sala.
        onlineClient.send({ type: "room:list" });
        return;
      }
    } finally {
      navigating = false;
    }
  };

  const handleMatchEnd = async (): Promise<void> => {
    if (!match || ending) return;
    ending = true;
    chrome.hideExit();
    hideObjective();
    // El chat del partido se va con el partido: en la pantalla de resultados
    // un ENTER abriria una linea escondida detras. Al volver a la sala, alli
    // esta la conversacion entera.
    matchChat?.dispose();
    matchChat = null;
    // En online: la sala a la que se vuelve, y el aviso al servidor de que se
    // ha visto el pitido final. El servidor no simula; sin esto no sabria que
    // el partido acabo y la sala seguiria "en partida".
    const onlineRoomId = activeLockstep ? onlineClient.activeRoomId : null;
    if (onlineRoomId && activeLockstep) {
      onlineClient.send({ type: "match:end", roomId: onlineRoomId, tick: activeLockstep.simulatedTicks });
    }
    const snapshot = {
      phase: match.state.phase,
      timeRemaining: match.state.timeRemaining,
      score: { ...match.state.score },
      goalPause: match.state.goalPause,
      lastScorer: match.state.lastScorer,
      offsideTeam: match.state.offsideTeam,
      offsideReview: match.state.offsideReview,
      goldenGoal: match.state.goldenGoal,
      period: match.state.period,
      periods: match.state.periods,
      breakPause: match.state.breakPause,
    };
    // La carrera se apunta antes de ensenar nada: el resultado tiene que poder
    // celebrar los logros que este mismo partido acaba de desbloquear.
    const stats = match.stats();
    const humanId = match.humanId;
    const side: TeamSide = humanId.startsWith("away") ? "away" : "home";
    const outcome = summariseMatch({
      snapshot: stats,
      humanId,
      side,
      score: snapshot.score,
      modeId: lastModeId,
      online: activeLockstep !== null,
      // Lo jugado de verdad, no lo programado: en gol de oro el partido dura
      // mas que `MATCH.DURATION`, y el logro de "marca en el ultimo minuto"
      // se mide contra esta cifra.
      durationSeconds: MATCH.DURATION - snapshot.timeRemaining,
      goldenGoal: snapshot.goldenGoal,
      // Contra la IA, lo buena que era la del rival: 1 si no se eligio nivel.
      aiLevel: lastMatchOptions.aiSkill?.[side === "home" ? "away" : "home"] ?? 1,
    });
    const profileSide = outcome.online ? "online" : "offline";
    // Dos en el mismo ordenador: un amistoso. No cuenta para las estadisticas
    // de nadie (el mando del jugador 1 lo pudo llevar cualquiera).
    const friendly = !outcome.online && lastMatchOptions.secondPlayerId !== undefined;
    const applied = friendly ? null : profileBook.of(profileSide).record(outcome);
    if (!friendly) profileSync.push(profileSide);
    // La carrera, si este era un partido de carrera. Cuenta tambien en tus
    // estadisticas offline (arriba), como cualquier partido contra la IA.
    const careerResult = careerRun ? recordCareerMatch(careerRun.setup, outcome) : null;

    const outcomeLabel =
      outcome.result === "win" ? t("Victoria") : outcome.result === "draw" ? t("Empate") : t("Derrota");

    // Los huecos van en el HTML del resumen; el contenido se monta justo
    // despues, cuando el DOM ya existe pero antes de esperar la decision.
    const resultPromise = chrome.showResult(snapshot, {
      summary: summaryLayoutHtml({
        you: yourMatchHtml(outcome.stat, outcome.isMvp, stats.mvp?.reason),
        // Sin tarjeta de MVP si el MVP eres tu: ya esta contada arriba.
        mvp: mvpBlockHtml(stats, humanId, { card: !outcome.isMvp }),
        // La media, el nivel, la barra de XP y los trofeos: todo eso lo monta
        // ya `careerRewardHtml`. Aqui solo se le da su columna.
        progress:
          (outcome.online && onlineMates.length > 0 ? `<div data-mates-slot></div>` : "") +
          (careerResult ?? "") +
          (applied ? careerRewardHtml(applied, applied.profile) : ""),
        extra: `<div data-clip-slot></div><div data-card-slot></div>`,
      }),
      stats: finalStatsHtml(stats, snapshot.score, humanId),
    }, onlineRoomId
      ? { primary: t("Volver a la sala"), secondary: t("Salir de la sala"), side }
      : careerRun
        ? // En la carrera no hay revancha: el resultado es el resultado.
          // "Continuar" lleva a la carrera: el movil, la tabla y el siguiente.
          { primary: t("Continuar"), secondary: t("Menú principal"), side }
        : { side });

    // Las invitaciones que llegaron jugando, ahora con su boton.
    social.flushDeferred();
    const matesSlot = document.querySelector("[data-mates-slot]") as HTMLElement | null;
    unmountMates?.();
    unmountMates = matesSlot ? mountMatesStrip(matesSlot, onlineMates, friendsStore) : null;

    const clip = match?.goalClip() ?? null;
    const clipSlot = document.querySelector("[data-clip-slot]") as HTMLElement | null;
    if (clip && clipSlot) {
      clipPlayer = createClipPlayer(clipSlot, clip);
      clipPlayer.play();
    }

    const cardSlot = document.querySelector("[data-card-slot]") as HTMLElement | null;
    if (cardSlot) {
      resultCard = mountResultCard(cardSlot, {
        homeScore: snapshot.score.home,
        awayScore: snapshot.score.away,
        modeLabel: MATCH_MODES[lastModeId].label,
        playerSide: side,
        nickname: outcome.stat?.nickname ?? t("Jugador"),
        goals: outcome.stat?.goals ?? 0,
        assists: outcome.stat?.assists ?? 0,
        isMvp: outcome.isMvp,
        outcome: outcomeLabel,
      });
    }

    const choice = await resultPromise;
    clipPlayer?.stop();
    unmountMates?.();
    unmountMates = null;
    if (onlineRoomId) {
      if (choice === "rematch") {
        await openHome({ roomId: onlineRoomId });
      } else {
        leaveRoom(onlineRoomId);
        await openHome();
      }
      return;
    }
    if (careerRun) {
      await openHome(choice === "rematch" ? { career: true } : {});
      return;
    }
    if (choice === "rematch") {
      const { network: _network, ...localOpts } = lastMatchOptions;
      startMatch(localOpts);
    } else {
      await openHome();
    }
  };

  /** Salir de la sala de verdad: sin esto, el asiento se quedaba ocupado por nadie. */
  const leaveRoom = (roomId: string | null): void => {
    if (!roomId) return;
    onlineClient.send({ type: "room:leave", roomId });
    roomChat.clear();
  };

  /** Dejar el partido en curso: en online es salir de la sala. */
  const exitMatch = (): void => {
    if (careerRun && match && !ending) {
      void leaveCareerMatch();
      return;
    }
    if (activeLockstep && !ending) leaveRoom(onlineClient.activeRoomId);
    void openHome();
  };

  /** Del partido a la pantalla de la sala, con un aviso. */
  const backToRoom = async (notice: string): Promise<void> => {
    const roomId = onlineClient.activeRoomId;
    if (!roomId) return;
    await openHome({ roomId, notice });
  };

  /*
   * Lo que manda el anfitrion y te afecta estando en el partido. En la
   * pantalla de resultados no se hace nada: el aviso llega al volver a la sala.
   */
  onlineClient.onMessage((msg) => {
    if (!match || ending || !activeLockstep) return;
    if (msg.type === "room:benched" && msg.roomId === onlineClient.activeRoomId) {
      void backToRoom(t("El anfitrión te ha pasado al banquillo"));
    }
    if (msg.type === "room:kicked" && msg.roomId === onlineClient.activeRoomId) {
      roomChat.clear();
      void openHome({ lobby: true, notice: t("Te han expulsado de la sala") });
    }
  });

  /*
   * En pleno partido, lo social llega como un aviso pequeño arriba: no se
   * puede aceptar nada con los pies ocupados, pero enterarse si. Al volver a
   * la sala o al lobby, alli esta todo.
   */
  const matchNotice = document.createElement("div");
  matchNotice.className = "match-notice";
  matchNotice.setAttribute("role", "status");
  document.body.appendChild(matchNotice);
  let matchNoticeTimer = 0;
  friendsStore.onEvent((event) => {
    if (!match || ending) return;
    const text =
      event.kind === "invite"
        ? t("{nick} te invita a {sala}", { nick: event.invite.from.nickname, sala: event.invite.roomName })
        : event.kind === "request"
          ? t("{nick} quiere ser tu amigo", { nick: event.from.nickname })
          : event.kind === "accepted"
            ? t("{nick} y tú ya sois amigos", { nick: event.from.nickname })
            : "";
    if (!text) return;
    showMatchNotice(text);
  });
  const showMatchNotice = (text: string): void => {
    matchNotice.textContent = text;
    matchNotice.classList.add("is-visible");
    clearTimeout(matchNoticeTimer);
    matchNoticeTimer = window.setTimeout(() => matchNotice.classList.remove("is-visible"), 5000);
  };
  // Un privado jugando: se lee arriba y se contesta al volver al lobby.
  directMessages.onIncoming((peer, entry) => {
    if (!match || ending) return;
    const nick = friendsStore.state()?.friends.find((f) => f.clientId === peer)?.nickname ?? "";
    showMatchNotice(`✉ ${nick}: ${entry.text}`);
  });

  chrome.setExitHandler(() => exitMatch());

  const onVisibilityChange = (): void => {
    if (document.hidden) {
      loop?.stop();
      loopPausedByVisibility = Boolean(match && !ending);
      match?.suspend();
      // Con la pestaña detras no suena nada: ni el menu ni el partido.
      menuMusic.stop();
      return;
    }
    if (match && !ending && loopPausedByVisibility && !careerLeaveOpen) {
      match.resume();
      // El frame que cruza una pausa dura lo que la pausa. `sample` ya descarta
      // los picos, pero es mas honesto no contarlo siquiera.
      lastFrameAt = 0;
      loop?.start();
      loopPausedByVisibility = false;
      return;
    }
    if (!match && mount.classList.contains("is-menu")) {
      menuMusic.play();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  const onPopState = (): void => {
    // Sin partido no se toca nada: el "atras" del menu tiene que seguir
    // sacando del juego, que es lo unico que puede significar ahi.
    if (!match || ending) return;
    exitMatch();
  };
  window.addEventListener("popstate", onPopState);

  /*
   * ESPACIO salta la celebracion y la repeticion.
   *
   * **Solo sin red.** `goalPause` es estado de la simulacion: si un cliente lo
   * saltara y el otro no, los dos partidos dejarian de ser el mismo desde ese
   * tick. En online el boton simplemente no hace nada, que es mejor que hacer
   * algo distinto en cada maquina.
   *
   * No salta la cuenta atras: esos tres segundos son para colocarse, y
   * quitarlos seria empezar a jugar sin estar listo.
   */
  window.addEventListener("keydown", (event) => {
    if (event.code !== "Space") return;
    if (!match || ending || activeLockstep) return;
    // Con dos jugadores, Espacio es el golpeo del 1: no se salta nada con el.
    if (lastMatchOptions.secondPlayerId) return;
    if (match.state.phase !== "goal" && match.state.phase !== "offside") return;
    event.preventDefault();
    match.skipGoalPause();
  });

  window.addEventListener("keydown", (event) => {
    if (event.code !== "Escape") return;
    /*
     * Mantener pulsado no repite la salida. Se mira `repeat` y no un cerrojo
     * que se quite al soltar: en pantalla completa el navegador se puede
     * quedar el `keyup` de Esc, y con el cerrojo puesto Esc dejaba de
     * funcionar hasta volver a pulsarlo y soltarlo.
     */
    if (event.repeat) return;
    if (match && !ending) {
      // Esc cierra el chat si se esta escribiendo; no te saca del partido.
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      exitMatch();
    }
  });

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      clearMatch();
      endCareerRun();
      menuMusic.dispose();
      chrome.dispose();
      mainMenu.dispose();
      careerScreen.dispose();
      privateMenu.dispose();
      careerHub.dispose();
      careerCloud.dispose();
      worldScreen.dispose();
      worldService.dispose();
      onlineLobby.dispose();
      onlineClient.dispose();
      disconnectOverlay.remove();
      netStatus.dispose();
      quality.dispose();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("popstate", onPopState);
      mobileShell.dispose();
      app.destroy(true, { children: true });
    });
  }

  const careerParam = parseCareerParam();
  if (careerParam) {
    await openHome({ career: true, careerCreate: careerParam === "new" });
    return;
  }

  const trainMode = parseTrainMode();
  if (trainMode) {
    setMatchMode(trainMode);
    lastModeId = trainMode;
    mount.classList.remove("is-menu");
    menuMusic.stop();
    startMatch({
      shirtNumber: 10,
      assist: "normal",
      pitchSkin: loadStoredPitchSkin(),
    });
    return;
  }

  await openHome();
}

boot().catch((error: unknown) => {
  console.error("[golazo] fallo al arrancar:", error);
});
